import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Form,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Spin,
  message,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { getData, postData } from "../../../../apiServices/common";
import {
  GET_FINISHING_LINE_MAPPING_INFO,
  GET_FINISHING_LINE_MAPPINGS,
} from "../../../../apiServices/API_ENDPOINTS";

const { Option } = Select;

const TabFolding = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [finishingLines, setFinishingLines] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existingMappings, setExistingMappings] = useState([]);
  // Track form errors for duplicate combinations
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  // Add new state for operator errors
  const [operatorErrors, setOperatorErrors] = useState({});

  const tabType = "Folding";

  // Expose fetchData method to parent component
  useImperativeHandle(ref, () => ({
    fetchData,
  }));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Call both APIs concurrently
      const [infoResponse, mappingsResponse] = await Promise.all([
        getData(GET_FINISHING_LINE_MAPPING_INFO, false, { type: tabType }),
        getData(GET_FINISHING_LINE_MAPPINGS, false, { type: tabType }),
      ]);

      // Handle mapping info response
      if (infoResponse?.data) {
        setFinishingLines(infoResponse.data.data.finishing_lines || []);
        setSupervisors(infoResponse.data.data.supervisors || []);
        setOperators(infoResponse.data.data.finishing_line_operators || []);
      }

      // Handle existing mappings response
      if (mappingsResponse?.data) {
        setExistingMappings(mappingsResponse.data.data || []);

        // Populate form with existing data if available
        if (
          mappingsResponse.data.data &&
          mappingsResponse.data.data.length > 0
        ) {
          const formData = {
            mappings: mappingsResponse.data.data.map((mapping) => ({
              finishing_line: mapping.finishing_line_id,
              supervisor: mapping.supervisor_id,
              finishing_line_operators: mapping.operators.map(
                (op) => op.operator_id,
              ),
            })),
          };

          form.setFieldsValue(formData);
        } else {
          // Reset to default single empty row if no data
          form.setFieldsValue({
            mappings: [{}],
          });
        }
      }
    } catch (error) {
      message.error("Failed to load Folding line mapping data");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    setFormSubmitting(true);

    // Validate for duplicate combinations before submitting
    const errors = validateForm();
    if (
      Object.keys(errors).length > 0 ||
      Object.keys(operatorErrors).length > 0
    ) {
      setFormErrors(errors);
      setFormSubmitting(false);
      message.error("Please fix validation errors before submitting");
      return;
    }

    setLoading(true);

    // Format the payload
    const payload = {
      type: tabType,
      mapping_details: values.mappings.map((item) => ({
        finishing_line_id: item.finishing_line,
        supervisor_id: item.supervisor,
        type: tabType,
        operators: item.finishing_line_operators.map((opId) => ({
          operator_id: opId,
        })),
      })),
    };

    // Add your API call to save the data
    postData(GET_FINISHING_LINE_MAPPINGS, payload)
      .then((response) => {
        if (response?.code === 200) {
          message.success("Folding line mapping saved successfully");
          // Refresh the data
          fetchData();
          // Clear any previous errors
          setFormErrors({});
        } else {
          throw new Error(response?.message || "Failed to save data");
        }
      })
      .catch((error) => {
        message.error("Failed to save Folding line mapping");
      })
      .finally(() => {
        setLoading(false);
        setFormSubmitting(false);
      });
  };

  // Update the validateForm function to check for duplicate operators
  const validateForm = () => {
    const errors = {};
    const formValues = form.getFieldsValue();
    const mappings = formValues.mappings || [];
    const combinations = {};
    const usedOperators = {};
    const operatorRowMapping = {};

    // First pass: check for duplicate line-supervisor combinations
    mappings.forEach((item, index) => {
      if (item.finishing_line && item.supervisor) {
        const key = `${item.finishing_line}-${item.supervisor}`;
        if (combinations[key]) {
          // This is a duplicate
          errors[`mappings.${index}.duplicate`] = true;
          errors[`mappings.${combinations[key] - 1}.duplicate`] = true;
        } else {
          combinations[key] = index + 1; // Store index+1 (for truthy checking)
        }
      }

      // Check for duplicate operators
      if (
        item.finishing_line_operators &&
        item.finishing_line_operators.length
      ) {
        item.finishing_line_operators.forEach((operatorId) => {
          if (usedOperators[operatorId]) {
            // This operator is used in multiple rows
            errors[`mappings.${index}.duplicate_operator`] = true;
            errors[
              `mappings.${usedOperators[operatorId] - 1}.duplicate_operator`
            ] = true;

            // Store which operators are duplicated in which rows
            if (!operatorRowMapping[operatorId]) {
              operatorRowMapping[operatorId] = [usedOperators[operatorId] - 1];
            }
            operatorRowMapping[operatorId].push(index);
          } else {
            usedOperators[operatorId] = index + 1; // Store index+1 (for truthy checking)
          }
        });
      }
    });

    // Update operator errors state
    setOperatorErrors(operatorRowMapping);

    return errors;
  };

  // Check if a line-supervisor combination is already used in another row
  const checkDuplicateCombination = (lineId, supervisorId, currentIndex) => {
    const formValues = form.getFieldsValue();
    const mappings = formValues.mappings || [];

    for (let i = 0; i < mappings.length; i++) {
      if (
        i !== currentIndex &&
        mappings[i]?.finishing_line === lineId &&
        mappings[i]?.supervisor === supervisorId
      ) {
        return true; // Found duplicate
      }
    }
    return false; // No duplicate found
  };

  // Handle finishing line selection
  const handleFinishingLineChange = (value, index) => {
    const formValues = form.getFieldsValue();
    const supervisorValue = formValues.mappings[index]?.supervisor;

    if (
      supervisorValue &&
      checkDuplicateCombination(value, supervisorValue, index)
    ) {
      // Update form errors state but don't reset the field value
      const newErrors = { ...formErrors };
      newErrors[`mappings.${index}.duplicate`] = true;
      setFormErrors(newErrors);

      // Show error message but keep the selected value
      message.error(
        "This combination of finishing line and supervisor is already used in another row",
      );
    } else {
      // Clear error for this field if it exists
      const newErrors = { ...formErrors };
      delete newErrors[`mappings.${index}.duplicate`];
      setFormErrors(newErrors);
    }
  };

  // Handle supervisor selection
  const handleSupervisorChange = (value, index) => {
    const formValues = form.getFieldsValue();
    const lineValue = formValues.mappings[index]?.finishing_line;

    if (lineValue && checkDuplicateCombination(lineValue, value, index)) {
      // Update form errors state but don't reset the field value
      const newErrors = { ...formErrors };
      newErrors[`mappings.${index}.duplicate`] = true;
      setFormErrors(newErrors);

      // Show error message but keep the selected value
      message.error(
        "This combination of finishing line and supervisor is already used in another row",
      );
    } else {
      // Clear error for this field if it exists
      const newErrors = { ...formErrors };
      delete newErrors[`mappings.${index}.duplicate`];
      setFormErrors(newErrors);
    }
  };

  // Handle operator selection
  const handleOperatorChange = (values, index) => {
    const formValues = form.getFieldsValue();
    const mappings = formValues.mappings || [];
    const newOperatorErrors = { ...operatorErrors };

    // Reset errors for this row
    Object.keys(newOperatorErrors).forEach((operatorId) => {
      if (newOperatorErrors[operatorId]?.includes(index)) {
        newOperatorErrors[operatorId] = newOperatorErrors[operatorId].filter(
          (idx) => idx !== index,
        );
        if (newOperatorErrors[operatorId].length === 0) {
          delete newOperatorErrors[operatorId];
        }
      }
    });

    // Get all currently used operators in other rows
    const usedOperatorsByRow = {};
    mappings.forEach((item, idx) => {
      if (idx !== index && item?.finishing_line_operators) {
        item.finishing_line_operators.forEach((operatorId) => {
          if (!usedOperatorsByRow[operatorId]) {
            usedOperatorsByRow[operatorId] = [];
          }
          usedOperatorsByRow[operatorId].push(idx);
        });
      }
    });

    // Check the currently selected operators against used operators
    values.forEach((operatorId) => {
      if (usedOperatorsByRow[operatorId]) {
        // Create or update error entry for this operator
        if (!newOperatorErrors[operatorId]) {
          newOperatorErrors[operatorId] = [];
        }

        // Add current row to error
        if (!newOperatorErrors[operatorId].includes(index)) {
          newOperatorErrors[operatorId].push(index);
        }

        // Add all other rows using this operator
        usedOperatorsByRow[operatorId].forEach((rowIdx) => {
          if (!newOperatorErrors[operatorId].includes(rowIdx)) {
            newOperatorErrors[operatorId].push(rowIdx);
          }
        });
      }
    });

    // Clean up any operator errors that no longer have duplicates
    Object.keys(newOperatorErrors).forEach((operatorId) => {
      if (newOperatorErrors[operatorId].length < 2) {
        delete newOperatorErrors[operatorId];
      }
    });

    setOperatorErrors(newOperatorErrors);

    // If there are errors, show warning
  };

  // Check if a specific mapping has an operator duplication error
  const hasOperatorError = (index) => {
    for (const operatorId in operatorErrors) {
      if (operatorErrors[operatorId].includes(index)) {
        return true;
      }
    }
    return false;
  };

  // Get duplicate operator error message
  const getOperatorErrorMessage = (index) => {
    const duplicates = [];
    for (const operatorId in operatorErrors) {
      if (operatorErrors[operatorId].includes(index)) {
        const operator = operators.find((op) => op.id === Number(operatorId));
        if (operator) {
          duplicates.push(operator.name);
        }
      }
    }

    if (duplicates.length > 0) {
      return `Operator${duplicates.length > 1 ? "s" : ""} ${duplicates.join(
        ", ",
      )} used in multiple rows`;
    }
    return "";
  };

  // Check if a specific mapping has a duplication error
  const hasDuplicationError = (index) => {
    return formErrors[`mappings.${index}.duplicate`] === true;
  };

  // Add a new function to check form values before submitting
  const handleSubmit = () => {
    // First manually validate all fields
    form
      .validateFields()
      .then((values) => {
        // Form validation passed, continue with submission
        onFinish(values);
      })
      .catch((errorInfo) => {
        // Form validation failed, show error
        message.error("Please fill all required fields");
        console.log("Validation failed:", errorInfo);
      });
  };

  // Function to determine field validation status that handles both built-in and custom validation
  const getValidationStatus = (fieldName, index) => {
    // Check for required field validation errors first
    const fieldErrors = form.getFieldError(["mappings", index, fieldName]);
    if (fieldErrors && fieldErrors.length > 0) {
      return "error";
    }

    // Then check for custom validation errors
    if (
      (fieldName === "finishing_line" || fieldName === "supervisor") &&
      hasDuplicationError(index)
    ) {
      return "error";
    } else if (
      fieldName === "finishing_line_operators" &&
      hasOperatorError(index)
    ) {
      return "error";
    }

    return "";
  };

  // Function to get appropriate help message, prioritizing required field validation
  const getHelpMessage = (fieldName, index) => {
    // Check for required field validation errors first
    const fieldErrors = form.getFieldError(["mappings", index, fieldName]);
    if (fieldErrors && fieldErrors.length > 0) {
      return fieldErrors[0];
    }

    // Then show custom validation messages
    if (
      (fieldName === "finishing_line" || fieldName === "supervisor") &&
      hasDuplicationError(index)
    ) {
      return "This combination is already used in another row";
    } else if (
      fieldName === "finishing_line_operators" &&
      hasOperatorError(index)
    ) {
      return getOperatorErrorMessage(index);
    }

    return "";
  };

  return (
    <Card title="Folding Line Mapping">
      <Spin spinning={loading}>
        <Form
          form={form}
          name="folding_line_mapping"
          layout="vertical"
          initialValues={{
            mappings: [{}], // Start with one empty row
          }}
          validateTrigger={["onChange", "onSubmit"]}
        >
          <Form.List name="mappings">
            {(fields, { add, remove: originalRemove }) => {
              // Create an enhanced remove function that also cleans up errors
              const enhancedRemove = (name, index) => {
                // First, clean up any operator errors for this row
                const newOperatorErrors = { ...operatorErrors };

                // Remove this row from any operator error entries
                Object.keys(newOperatorErrors).forEach((operatorId) => {
                  if (newOperatorErrors[operatorId]?.includes(index)) {
                    // Remove this index from the array
                    newOperatorErrors[operatorId] = newOperatorErrors[
                      operatorId
                    ].filter((idx) => idx !== index);

                    // Adjust indices for rows after the removed row
                    newOperatorErrors[operatorId] = newOperatorErrors[
                      operatorId
                    ].map((idx) => (idx > index ? idx - 1 : idx));

                    // Delete the entry if no more errors
                    if (newOperatorErrors[operatorId].length < 2) {
                      delete newOperatorErrors[operatorId];
                    }
                  }
                });

                // Also update formErrors
                const newFormErrors = { ...formErrors };
                Object.keys(newFormErrors).forEach((key) => {
                  // Remove errors for this index
                  if (key.startsWith(`mappings.${index}.`)) {
                    delete newFormErrors[key];
                  }

                  // Adjust indices for rows after the removed row
                  const match = key.match(/^mappings\.(\d+)\./);
                  if (match && parseInt(match[1]) > index) {
                    const newIndex = parseInt(match[1]) - 1;
                    const newKey = key.replace(
                      `mappings.${match[1]}.`,
                      `mappings.${newIndex}.`,
                    );
                    newFormErrors[newKey] = newFormErrors[key];
                    delete newFormErrors[key];
                  }
                });

                // Update states
                setOperatorErrors(newOperatorErrors);
                setFormErrors(newFormErrors);

                // Call the original remove function
                originalRemove(name);

                // Re-validate the form after a short delay to ensure UI has updated
                setTimeout(() => {
                  validateForm();
                }, 100);
              };

              return (
                <>
                  {/* Table header */}
                  <Row gutter={16} style={{ marginBottom: 8 }}>
                    <Col span={6}>
                      <Typography.Text strong>
                        Finishing Line <span style={{ color: "red" }}>*</span>
                      </Typography.Text>
                    </Col>
                    <Col span={6}>
                      <Typography.Text strong>
                        Supervisor <span style={{ color: "red" }}>*</span>
                      </Typography.Text>
                    </Col>
                    <Col span={11}>
                      <Typography.Text strong>
                        Operators <span style={{ color: "red" }}>*</span>
                      </Typography.Text>
                    </Col>
                  </Row>

                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row key={key} gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "finishing_line"]}
                          rules={[
                            { required: true, message: "Required field" },
                          ]}
                          style={{ marginBottom: 0 }}
                          validateStatus={getValidationStatus(
                            "finishing_line",
                            index,
                          )}
                          help={getHelpMessage("finishing_line", index)}
                        >
                          <Select
                            placeholder="Select Finishing Line"
                            loading={loading}
                            size="small"
                            onChange={(value) =>
                              handleFinishingLineChange(value, index)
                            }
                            status={
                              getValidationStatus("finishing_line", index) ===
                              "error"
                                ? "error"
                                : ""
                            }
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                              // Handle both Option component children and options prop
                              const optionText =
                                option.children || option.label || "";
                              return String(optionText)
                                .toLowerCase()
                                .includes(input.toLowerCase());
                            }}
                            className="search-enabled-select"
                          >
                            {finishingLines.map((line) => (
                              <Option key={line.id} value={line.id}>
                                {line.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "supervisor"]}
                          rules={[
                            { required: true, message: "Required field" },
                          ]}
                          style={{ marginBottom: 0 }}
                          validateStatus={getValidationStatus(
                            "supervisor",
                            index,
                          )}
                          help={getHelpMessage("supervisor", index)}
                        >
                          <Select
                            placeholder="Select Supervisor"
                            loading={loading}
                            size="small"
                            onChange={(value) =>
                              handleSupervisorChange(value, index)
                            }
                            status={
                              getValidationStatus("supervisor", index) ===
                              "error"
                                ? "error"
                                : ""
                            }
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            className="search-enabled-select"
                          >
                            {supervisors.map((supervisor) => (
                              <Option key={supervisor.id} value={supervisor.id}>
                                {supervisor.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item
                          {...restField}
                          name={[name, "finishing_line_operators"]}
                          rules={[
                            { required: true, message: "Required field" },
                          ]}
                          style={{ marginBottom: 0 }}
                          validateStatus={
                            hasOperatorError(index) ? "error" : ""
                          }
                          help={
                            hasOperatorError(index)
                              ? getOperatorErrorMessage(index)
                              : ""
                          }
                        >
                          <Select
                            mode="multiple"
                            placeholder="Select Operators"
                            loading={loading}
                            style={{ width: "100%" }}
                            size="small"
                            onChange={(values) =>
                              handleOperatorChange(values, index)
                            }
                            status={hasOperatorError(index) ? "error" : ""}
                            showSearch
                            optionFilterProp="children"
                            // filterOption={(input, option) =>
                            //   option.children
                            //     .toLowerCase()
                            //     .includes(input.toLowerCase())
                            // }
                            className="search-enabled-select"
                          >
                            {operators.map((operator) => (
                              <Option key={operator.id} value={operator.id}>
                                {operator.name} ({operator.id_card_no})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <Space>
                          {fields.length > 1 && (
                            <Popconfirm
                              title="Remove this mapping"
                              description="Are you sure you want to remove this mapping?"
                              onConfirm={() => enhancedRemove(name, index)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <MinusCircleOutlined
                                style={{
                                  color: "red",
                                  fontSize: "16px",
                                  marginTop: 8,
                                }}
                                size={10}
                              />
                            </Popconfirm>
                          )}
                          {index === fields.length - 1 && (
                            <PlusCircleOutlined
                              onClick={() => add()}
                              style={{
                                color: "#1890ff",
                                fontSize: "16px",
                                marginTop: 8,
                              }}
                              size={10}
                            />
                          )}
                        </Space>
                      </Col>
                    </Row>
                  ))}
                </>
              );
            }}
          </Form.List>

          {(Object.keys(formErrors).length > 0 ||
            Object.keys(operatorErrors).length > 0) && (
            <div
              style={{
                color: "#ff4d4f",
                marginBottom: 16,
                backgroundColor: "#fff2f0",
                padding: "8px 12px",
                border: "1px solid #ffccc7",
                borderRadius: "2px",
              }}
            >
              {Object.keys(formErrors).length > 0 && (
                <div>
                  Please fix the duplicate combinations before submitting
                </div>
              )}
              {Object.keys(operatorErrors).length > 0 && (
                <div>
                  Each operator can only be assigned to one finishing line
                </div>
              )}
            </div>
          )}

          <Form.Item>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading || formSubmitting}
              disabled={
                Object.keys(formErrors).length > 0 ||
                Object.keys(operatorErrors).length > 0
              }
            >
              Save
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
});

export default TabFolding;
